import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./RewardsHome.module.css";
import {
  getAppSumoStatus,
  getAppSumoFeedback,
  getAppSumoReferrals,
} from "../../Store/apiStore";
import {
  ChevronIcon,
  ClockIcon,
  StarIcon,
  PeopleIcon,
  ShareIcon,
  LinkIcon,
  CopyIcon,
} from "./icons";

/**
 * "Rewards & Referrals" — the reward hub, reached from the rewards icon on the
 * agent detail screen.
 *
 * Shows what this screen itself has earned the user — the bonus and referral
 * pools only — and routes to the two ways of adding to it. The starter pool is
 * deliberately left out: it came with the AppSumo code, not from giving
 * feedback or referring anyone, so counting it here would credit this screen
 * with minutes the user already had before they ever opened it.
 */
const RewardsHome = () => {
  const navigate = useNavigate();
  const [balanceSeconds, setBalanceSeconds] = useState(null);
  // Lifetime earned across the same two pools. The balance on its own does not
  // say whether it is untouched or what is left of something larger, so it is
  // shown as "remaining / earned".
  const [earnedSeconds, setEarnedSeconds] = useState(null);
  // What the feedback bonus is worth to THIS account — it matches their tier,
  // so a fixed figure here would be wrong for two buyers in three.
  const [bonusMinutes, setBonusMinutes] = useState(null);
  // The referral code, for the share box at the foot of the page.
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getAppSumoStatus()
      .then((d) => {
        // Both pools, and only these two: totalSeconds folds in the starter
        // pool the code itself granted, which this screen did not earn.
        const b = d?.balances || {};
        if (!cancelled) {
          setBalanceSeconds(
            (Number(b.bonusSeconds) || 0) + (Number(b.referralSeconds) || 0)
          );
          // Lifetime earned, shown as the denominator. Older backends do not
          // send it — left null there, and the tile falls back to the single
          // figure it showed before.
          setEarnedSeconds(
            b.earnedSeconds === undefined ? null : Number(b.earnedSeconds) || 0
          );
        }
      })
      .catch(() => {
        // A failed lookup should not blank the page — the two earn routes below
        // work regardless of what the balance is.
        if (!cancelled) setBalanceSeconds(0);
      });

    getAppSumoFeedback()
      .then((d) => {
        if (!cancelled) setBonusMinutes(d?.bonusMinutes ?? null);
      })
      .catch(() => {
        if (!cancelled) setBonusMinutes(null);
      });

    getAppSumoReferrals()
      .then((d) => {
        if (!cancelled) setReferral(d);
      })
      .catch(() => {
        if (!cancelled) setReferral(null);
      });

    return () => {
      cancelled = true;
      if (copyTimer.current) clearTimeout(copyTimer.current);
    };
  }, []);

  const minutes =
    balanceSeconds === null ? null : Math.floor(balanceSeconds / 60);
  const earnedMinutes =
    earnedSeconds === null ? null : Math.floor(earnedSeconds / 60);
  // Only worth two figures once something has actually been earned, and only
  // when they differ — "0 / 0" and "750 / 750" both say less than one number.
  const showTotal =
    earnedMinutes !== null && earnedMinutes > 0 && earnedMinutes !== minutes;

  // Built the same way ReferralsEarnings builds it, so a link copied from
  // either screen is identical: current origin (dev stays dev), vanity name
  // ahead of the raw code.
  const link = useMemo(() => {
    const slug = referral?.referralName || referral?.referralCode;
    if (!slug) return "";
    return `${window.location.origin}?referral=${encodeURIComponent(slug)}`;
  }, [referral]);

  /*
   * Dono earn-rows ka markup bilkul ek jaisa hai, sirf content alag — isliye
   * data yahan aur JSX me ek hi .map(). Teesra tarika jodna ho to sirf yahan
   * ek object add karna hoga.
   */
  const earnActions = [
    {
      key: "feedback",
      title: "Give Feedback",
      description: "Share your feedback on trusted platforms and earn minutes",
      // Tier ke hisaab se — API se aane tak generic line dikhti hai.
      reward: bonusMinutes ? `Earn up to ${bonusMinutes} mins` : "Earn minutes",
      icon: <StarIcon />,
      to: "/rewards/feedback",
    },
    {
      key: "refer",
      title: "Refer & Earn",
      description:
        "Refer Rexpt to your friends and earn minutes on their purchase",
      reward: "Earn unlimited minutes",
      icon: <PeopleIcon />,
      to: "/rewards/referrals",
    },
  ];

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard is blocked outside a secure context; select the field so the
      // user can copy by hand rather than leaving them with nothing.
      document.getElementById("rewards-referral-link")?.select?.();
      return;
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1800);
  };

  const handleShare = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Rexpt — AI Receptionist",
          text: "Never miss a business call again. Try Rexpt:",
          url: link,
        });
        return;
      } catch {
        /* dismissed — fall through to copy */
      }
    }
    handleCopy();
  };

  return (
    <div className={styles.page}>
      {/* Back only. HeaderBar is deliberately not used here: with an empty
          title it still reserves a full title row, which pushed the heading
          down for no reason. The centred heading below already names the
          screen, so this just needs to be the way out. */}
      <div className={styles.topBar}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ChevronIcon width={18} height={18} className={styles.backChevron} />
        </button>
      </div>

      <h1 className={styles.heading}>Rewards &amp; Referrals</h1>
      <p className={styles.subheading}>
        Earn extra AI Call minutes through <br /> feedback and referrals
      </p>

      <div className={styles.earnedCard}>
        <span className={styles.tileClock}>
          <ClockIcon width={24} height={24} />
        </span>

        <div className={styles.earnedCopy}>
          {/* "Earned Minutes" alone was read as the lifetime total, so a
              part-spent balance looked like the wrong number. The label now
              names the figure in front, and the total follows it. */}
          <p className={styles.earnedLabel}>
            {showTotal ? "Minutes Remaining" : "Earned Minutes"}
          </p>
          <p className={styles.earnedValue}>
            {minutes === null ? "—" : minutes.toLocaleString()}
            {showTotal ? (
              <span className={styles.earnedTotal}>
                {" / "}
                {earnedMinutes.toLocaleString()} earned
              </span>
            ) : (
              " mins"
            )}
          </p>
        </div>

        {/* The breakdown lives on its own screen now that the referrals page no
            longer carries an Earnings tab; this is where the total is, so this
            is where the way into it belongs. The label is on the button rather
            than beside it — the card reads as one row, and a screen reader
            still hears where the chevron goes. */}
        <button
          type="button"
          className={styles.earnedChevron}
          onClick={() => navigate("/rewards/earnings")}
          aria-label="View earnings"
        >
          <ChevronIcon width={18} height={18} strokeWidth={2.4} />
        </button>
      </div>

      <div className={styles.waysRow}>
        <h2 className={styles.waysTitle}>Ways to earn</h2>
      </div>

      {earnActions.map((action) => (
        <button
          key={action.key}
          type="button"
          className={styles.actionCard}
          onClick={() => navigate(action.to)}
        >
          <span className={styles.tileAction}>{action.icon}</span>
          <span className={styles.actionBody}>
            <span className={styles.actionTitle}>{action.title}</span>
            <span className={styles.actionDesc}>{action.description}</span>
            <span className={styles.actionReward}>
              <ClockIcon width={14} height={14} />
              {action.reward}
            </span>
          </span>
          <span className={styles.tileChevron}>
            <ChevronIcon width={14} height={14} />
          </span>
        </button>
      ))}

      {/* Rendered only once the code has loaded: an empty field with a Copy
          button that copies nothing is worse than no box at all. */}
      {link && (
        <div className={styles.referralCard}>
          <div className={styles.referralHead}>
            <p className={styles.referralLabel}>~Your referral link</p>
            <button
              type="button"
              className={styles.tileShare}
              onClick={handleShare}
              aria-label="Share referral link"
            >
              <ShareIcon />
            </button>
          </div>

          <div className={styles.linkRow}>
            <span className={styles.linkIcon}>
              <LinkIcon />
            </span>
            <input
              id="rewards-referral-link"
              className={styles.linkText}
              value={link}
              readOnly
              onFocus={(e) => e.target.select()}
            />
            <span className={styles.copyWrap}>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopy}
              >
                <CopyIcon />
                Copy
              </button>
              {copied && <span className={styles.tooltip}>Copied!</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsHome;
